import { injectTenantInURL } from './request';
import { session } from '../session';
import { requestWithStatus } from './base_request';
import { Deferred } from '../utils/deferred';

export interface TaskCopyTrial {
  trial_id: number;
  trial_name: string;
}

export interface TaskCopyError extends TaskCopyTrial {
  error: string;
}

export interface TaskCopyDetails {
  succeeded: TaskCopyTrial[];
  failed: TaskCopyError[];
}

export interface TaskStatus {
  status: string;
  completion_perc: number;
  download_url: string;
  error_msg: string;
  details: TaskCopyDetails | null;
}

export function pollTask(taskId: string): Promise<TaskStatus> {
  // ignore errors when polling
  let url = injectTenantInURL('/api/tasks/' + taskId + '/poll_task/');
  return session
    .getToken()
    .then((token) => requestWithStatus<TaskStatus>('GET', url, token))
    .then((res) => {
      if (res.status == 200) {
        return res.result;
      }
    });
}

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_MAX_CONSECUTIVE_FAILURES = 5;

export class PollTaskConnectionLost extends Error {
  constructor(public readonly consecutiveFailures: number) {
    super(`Lost connection after ${consecutiveFailures} consecutive poll failures`);
    this.name = 'PollTaskConnectionLost';
  }
}

export class PollTaskCancelled extends Error {
  constructor() {
    super('Task polling cancelled');
    this.name = 'PollTaskCancelled';
  }
}

export interface PollTaskOptions {
  intervalMs?: number;
  maxConsecutiveFailures?: number;
  onProgress?: (completionPercent: number) => void;
}

export interface PollTaskHandle {
  promise: Promise<TaskStatus>;
  cancel: () => void;
}

/**
 * Resolves with the final TaskStatus on task_done. After
 * maxConsecutiveFailures consecutive pollTask rejections, rejects with
 * PollTaskConnectionLost. cancel() rejects with PollTaskCancelled - call
 * it from the component's dispose(). Transient non-2xx responses
 * (pollTask returns undefined) don't count as failures.
 */
export function pollTaskUntilDone(taskId: string, options: PollTaskOptions = {}): PollTaskHandle {
  const intervalMs = options.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxFailures = options.maxConsecutiveFailures ?? DEFAULT_MAX_CONSECUTIVE_FAILURES;
  const deferred = new Deferred<TaskStatus, Error>();
  const state = {
    timeoutToken: null as number | null,
    cancelled: false,
    consecutiveFailures: 0,
  };

  const schedule = () => {
    state.timeoutToken = window.setTimeout(tick, intervalMs);
  };

  const tick = async () => {
    state.timeoutToken = null;
    if (state.cancelled) return;

    try {
      const res = await pollTask(taskId);
      if (state.cancelled) return;
      state.consecutiveFailures = 0;

      if (!res) {
        schedule();
        return;
      }
      if (res.status !== 'task_done') {
        options.onProgress?.(res.completion_perc ?? 0);
        schedule();
        return;
      }
      deferred.resolve(res);
    } catch (error) {
      if (state.cancelled) return;
      state.consecutiveFailures += 1;
      if (state.consecutiveFailures >= maxFailures) {
        deferred.reject(new PollTaskConnectionLost(state.consecutiveFailures));
        return;
      }
      schedule();
    }
  };

  schedule();

  return {
    promise: deferred.promise,
    cancel: () => {
      if (state.cancelled) return;
      state.cancelled = true;
      if (state.timeoutToken !== null) {
        clearTimeout(state.timeoutToken);
        state.timeoutToken = null;
      }
      deferred.reject(new PollTaskCancelled());
    },
  };
}
